// Design-system reminder: every title uses a typography.h* preset, OR `font-heading font-bold`.
//
// Single source of the published Partner Program Terms text. Rendered both in
// the partner portal acceptance gate and on the public /partner-terms page, so
// a partner reads exactly the text they accept.
//
// When the text changes materially, bump PARTNER_TERMS_VERSION on the backend
// (portfolio_creator/portfolio_creator/settings.py) and the constants below, so
// partners are asked to accept the new version and the acceptance history
// records which text was in force.
import React from "react";
import { typography, textColors } from "../../design-system/tokens";

export const PARTNER_TERMS_DOCUMENT_ID = "PARTNER-PROGRAM-TERMS-2026-08-17";
export const PARTNER_TERMS_VERSION = "2026-08-17";
export const PARTNER_TERMS_LAST_UPDATED = "August 17, 2026";

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className={`${typography.h4} ${textColors.primary}`}>{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className={`${typography.bodySmall} ${textColors.secondary} leading-relaxed`}>{children}</p>
);

const List = ({ items }) => (
  <ul className={`list-disc pl-5 space-y-1 ${typography.bodySmall} ${textColors.secondary}`}>
    {items.map((item, index) => (
      <li key={index}>{item}</li>
    ))}
  </ul>
);

const PartnerTermsContent = () => (
  <div className="space-y-6">
    <P>
      These Partner Program Terms of Use ("Partner Terms") govern your participation in the
      myvitae.ai partner program (the "Program"), operated by Vitae AI LLC ("Company", "we", "us").
      They are a separate agreement from, and in addition to, the myvitae.ai Terms of Use and
      Privacy Policy, which continue to apply to your use of the service. Document{" "}
      {PARTNER_TERMS_DOCUMENT_ID}, version {PARTNER_TERMS_VERSION}.
    </P>

    <Section title="1. Who partners are">
      <P>
        A "Partner" is an organization or individual that the Company has granted access to the
        partner portal in order to receive or purchase packs of myvitae.ai coupon codes ("Coupons")
        and distribute them to its own members, students, employees, customers, or audience.
        Typical partners include educational institutions, career services, communities,
        associations, and businesses. Partners are not affiliates: unless we agree otherwise in a
        separate written agreement, partners do not earn commission on payments made by the people
        they refer.
      </P>
      <P>
        Participation is at our discretion, and being a myvitae.ai user does not entitle you to
        partner status. We may decline, limit, or revoke partner status at any time as described in
        section 11.
      </P>
    </Section>

    <Section title="2. Acceptance and enrollment">
      <P>
        You must accept these Partner Terms before you can use the partner portal or purchase
        Coupon packs. We record your acceptance electronically, including the version, date and
        time, IP address, and browser, and we keep a history of every version you have accepted.
      </P>
      <P>
        If you accept on behalf of a company, institution, or other organization, you confirm you
        have authority to bind it, and "you" in these Partner Terms means that organization. Where
        these Partner Terms conflict with the general Terms of Use on Program matters, these
        Partner Terms control.
      </P>
    </Section>

    <Section title="3. Eligibility and account terms">
      <P>
        You must be at least 18 years old and legally able to enter into contracts. You must not be
        located in, or a resident of, a jurisdiction subject to comprehensive sanctions, and you
        must not appear on an applicable restricted-party list. You must keep one partner account
        unless we agree otherwise in writing, keep your contact and billing details accurate, and
        safeguard your credentials — you are responsible for all activity under your account,
        including purchases made through it.
      </P>
      <P>
        You are an independent contractor. Nothing here creates an employment, partnership, joint
        venture, franchise, reseller, or agency relationship. You may not make representations or
        commitments on our behalf, or present yourself as our employee, agent, official reseller,
        support channel, or spokesperson.
      </P>
    </Section>

    <Section title="4. Portal access and linked-user tracking">
      <P>
        The partner portal shows you the Coupons linked to your account, how many people have
        signed up or purchased through your Coupons and links ("linked users"), and related
        aggregate analytics. A user becomes linked to you when they register through one of your
        Coupon links or apply one of your Coupon codes.
      </P>
      <P>
        <strong>To protect user privacy, the portal never discloses the identity or personal data
        of linked users.</strong> You see counts and aggregate statistics only. You may not attempt
        to identify, contact, or re-target linked users using Company data, systems, or inferences
        drawn from them. Our tracking and records are the authoritative record of linkages and
        redemptions, absent manifest error; please raise any discrepancy in writing within 60 days.
      </P>
    </Section>

    <Section title="5. Coupon packs and pricing">
      <P>
        The portal lets you purchase packs of Coupons in a single, one-time payment. Before you
        pay, the portal shows you a full quote: the number of Coupons per plan, the per-Coupon
        price, any volume discount, the access period each Coupon grants, and the total in U.S.
        dollars. The price you see in the quote at the moment of purchase is the price you pay.
      </P>
      <P>
        Payment is processed by Stripe as a one-time charge — Coupon packs are not a subscription
        and do not renew. <strong>Coupons are generated only after your payment succeeds.</strong>{" "}
        Once payment is confirmed, your Coupons are created and appear in the "My Coupons" section
        of the portal, usually within a few minutes. If your Coupons have not appeared within 24
        hours of a successful payment, contact us and we will resolve it.
      </P>
      <P>
        Per-Coupon prices, volume discount tiers, and pack structure are set by us and{" "}
        <strong>may change at any time without notice</strong>. Changes never affect packs you have
        already paid for; they apply only to future quotes and purchases. We may also offer
        specific partners individual pricing at our discretion.
      </P>
    </Section>

    <Section title="6. What a Coupon grants">
      <P>Each Coupon generated from a pack:</P>
      <List
        items={[
          "is a single-use code — it can be redeemed by exactly one user, once;",
          "grants the redeeming user free access to the plan the Coupon was purchased for (Plus or Pro) for the access period shown in the quote at purchase time — the benefits are those displayed at purchase time;",
          "may have an expiry or validity window where indicated in the portal.",
        ]}
      />
      <P>
        Coupon benefits are defined at generation time and are not changed retroactively by later
        pricing or Program changes. Coupons have no cash value, are not legal tender, and cannot be
        exchanged for money by you or by the redeeming user.
      </P>
    </Section>

    <Section title="7. Free Coupons">
      <P>
        We may, entirely at our discretion, grant a partner free Coupons — for example as part of a
        pilot, sponsorship, or institutional arrangement. Free Coupons are a courtesy, not an
        entitlement: we decide their number, plan, access period, and validity, we may attach
        additional conditions to them, and we may decline to issue further free Coupons at any
        time. Except where these Partner Terms distinguish them, free Coupons are subject to the
        same rules as purchased Coupons, including the distribution rules in section 8.
      </P>
    </Section>

    <Section title="8. Distribution responsibilities">
      <P>
        You decide how to distribute your Coupons to your audience, and you are responsible for
        that distribution. You must comply with all applicable laws in doing so, including
        advertising, consumer-protection, privacy, email, and messaging laws. You must not:
      </P>
      <List
        items={[
          "misrepresent what a Coupon grants — its plan, access period, discount, single-use nature, or any expiry — or misstate the price, features, or nature of the myvitae.ai service;",
          "make false, misleading, or unsupported claims about the service — in particular, never promise or imply that using myvitae.ai will result in employment, job offers, interviews, admission, scholarships, visas, or any specific professional or academic result;",
          "sell, auction, or otherwise trade Coupons on unauthorized resale markets, coupon-trading sites, or code marketplaces, or distribute them through channels we have told you in writing not to use;",
          "distribute Coupons through spam, purchased contact lists, bots, or automated bulk messaging that violates applicable opt-out and sender-identification rules;",
          "present yourself as the Company, its support, or an official reseller, or run lookalike sites or accounts;",
          "use Coupons or Program materials alongside illegal, hateful, harassing, sexually explicit, defamatory, or infringing content;",
          "attempt to manipulate, probe, or interfere with our coupon, tracking, payment, or portal systems, including generating redemptions through fictitious or coordinated accounts.",
        ]}
      />
      <P>
        Charging your own audience for access to benefits that include a Coupon (for example, a
        membership that includes a Coupon as one perk) is permitted, provided you do not present
        the Coupon itself as a product you sell and you comply with the rest of this section. If
        you are unsure whether a distribution model is allowed, ask us first at legal@myvitae.ai.
      </P>
      <P>
        On request, you must tell us within 5 business days how and where your Coupons are being
        distributed. If you breach this section, we may void affected Coupons, suspend your
        account during investigation, and terminate your participation for cause.
      </P>
    </Section>

    <Section title="9. Refunds">
      <P>
        <strong>Coupon pack purchases are non-refundable once the Coupons have been generated</strong>,
        except where a refund is required by applicable law. Coupons are generated immediately
        after successful payment, so in practice a completed purchase is final. If a payment
        succeeds but Coupons are not generated due to a fault on our side, we will either generate
        the Coupons or refund the payment in full, at your choice. Unused or expired Coupons are
        not refunded, exchanged, or re-credited. If you charge back or reverse a payment for a pack
        whose Coupons were generated, we may cancel the remaining unredeemed Coupons from that pack
        and suspend your portal access while we investigate.
      </P>
    </Section>

    <Section title="10. Taxes">
      <P>
        Prices shown in the portal are exclusive of taxes unless stated otherwise. You are
        responsible for any sales, use, VAT, GST, withholding, or similar taxes arising from your
        purchases or from your own distribution arrangements, except taxes on our net income. Where
        we are required to collect tax on a purchase, it will be added at checkout or invoiced to
        you. You are solely responsible for the tax treatment of any consideration you receive from
        your own audience in connection with Coupons.
      </P>
    </Section>

    <Section title="11. Suspension, termination, and portal blocking">
      <P>
        These Partner Terms apply from your acceptance until participation ends. You may leave the
        Program at any time by written notice. We may terminate or suspend your participation for
        convenience on 14 days' notice, and immediately for cause — including fraud, breach of
        section 8, misuse of our marks, chargeback abuse, legal-compliance concerns, or conduct
        that harms the service, its users, or our reputation.
      </P>
      <P>
        We may also <strong>block access to the partner portal</strong> — temporarily or
        permanently — while we investigate suspected abuse or a breach of these Partner Terms.
        While blocked, you cannot view analytics or purchase packs, but Coupons already redeemed by
        users remain valid for those users.
      </P>
      <P>
        On termination for convenience or by you, Coupons already generated remain redeemable
        until they are used or expire, unless we cancel them for cause. On termination for cause,
        we may cancel unredeemed Coupons attributable to the breach. Users who already redeemed a
        Coupon keep the benefits they received. Sections 4 (privacy restrictions), 9, 10, and 12
        through 16 survive termination.
      </P>
    </Section>

    <Section title="12. Trademarks, brand assets, and intellectual property">
      <P>
        We grant you a limited, revocable, non-exclusive, non-transferable, non-sublicensable
        licence to use the brand assets and materials we provide, solely to distribute your Coupons
        and describe the Program while you participate, and only in the form provided or approved.
        We reserve all other rights: you gain no ownership or goodwill in our marks, must not seek
        to register them, and must not register or use domains, social handles, app names, or
        business names containing our marks or confusingly similar variants. We may require you to
        change or remove Program content we consider inaccurate, non-compliant, or damaging to our
        reputation. On termination, the licence ends and you must remove our marks, materials, and
        Coupon links from your channels within 7 days. You are responsible for your own channels
        and content and must hold the rights to any third-party material you publish.
      </P>
    </Section>

    <Section title="13. Confidentiality and data protection">
      <P>
        Non-public Program information — including negotiated pricing, unreleased features, Program
        metrics, and anything marked or reasonably understood as confidential — must not be
        disclosed or used outside the Program. Each party is an independent controller of the
        personal data it processes for its own purposes; you are responsible for the lawfulness of
        your own distribution lists, consents, and privacy notices. You must not use Company
        systems to obtain personal data about users of the service, and you must tell us promptly
        about any security incident affecting your Program credentials or Coupons.
      </P>
    </Section>

    <Section title="14. Disclaimers and limitation of liability">
      <P>
        The Program and partner portal are provided "as is" and "as available", without warranties
        of any kind, including merchantability, fitness for a particular purpose, non-infringement,
        uninterrupted tracking, or the accuracy of reporting. We do not warrant that tracking
        captures every linked user, and we are not responsible for linkages lost through incorrect
        links or codes, privacy settings, ad blockers, browser behavior, or third-party platform
        changes.
      </P>
      <P>
        To the fullest extent permitted by law, neither party is liable for indirect, incidental,
        special, consequential, exemplary, or punitive damages, or for lost profits, revenue, or
        goodwill. Our total aggregate liability relating to the Program will not exceed the total
        amounts you actually paid us for Coupon packs in the 12 months before the event giving rise
        to the claim, or US$100 if you paid nothing in that period. That cap does not limit your
        indemnification obligations, liability for fraud or wilful misconduct, or liability for
        infringement or misuse of our intellectual property.
      </P>
      <P>
        You will indemnify, defend, and hold harmless the Company and its members, managers,
        officers, employees, and agents against claims, investigations, fines, penalties, losses,
        damages, liabilities, and reasonable legal fees arising from: your distribution of Coupons,
        your content, statements, or omissions; your breach of these Partner Terms or of law; your
        use or misuse of our marks or materials; any claim that you are our employee or agent; and
        your handling of personal data.
      </P>
    </Section>

    <Section title="15. Changes to these Partner Terms and the Program">
      <P>
        We may amend these Partner Terms by publishing an updated version with a new version
        identifier and notifying you by email and/or in the portal. Material changes require you to
        accept the new version in the portal before continuing to use it; continuing to participate
        on or after the effective date also means you accept it. Changes apply going forward and do
        not affect Coupon packs already paid for or Coupons already generated. We keep your
        acceptance history and prior versions, available on request. We may modify, suspend, or
        discontinue the Program, the portal, pack pricing, or any Coupon feature at any time; if we
        discontinue the Program, section 11 applies as if we had terminated for convenience.
      </P>
    </Section>

    <Section title="16. Governing law, disputes, and general">
      <P>
        These Partner Terms are governed by the laws of the State of Delaware and the United
        States, without regard to conflict-of-law principles. Before starting formal proceedings,
        you agree to contact us at legal@myvitae.ai with a written description of the dispute and
        the relief sought, and to try in good faith to resolve it for 30 days. Except for claims
        eligible for small claims court, claims for injunctive or equitable relief about
        intellectual property, unauthorized access, or confidential information, or claims where
        mandatory arbitration is prohibited by law, disputes relating to the Program will be
        resolved by final and binding individual arbitration administered by the American
        Arbitration Association under its Commercial Arbitration Rules, in English, seated in
        Delaware. TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AND THE COMPANY EACH WAIVE ANY RIGHT
        TO A JURY TRIAL AND ANY RIGHT TO BRING OR PARTICIPATE IN A CLASS, COLLECTIVE, CONSOLIDATED,
        OR REPRESENTATIVE PROCEEDING.
      </P>
      <P>
        These Partner Terms, with any Program agreement we sign with you, are the entire agreement
        about the Program; a signed agreement controls for that partner where it conflicts. We may
        give notice by email to your account address or in the portal, and you consent to
        electronic Program notices; notices to us go to legal@myvitae.ai. You may not assign your
        participation or Coupons without our written consent, and we may assign these Partner Terms
        in a merger, acquisition, financing, or sale of assets. If a provision is unenforceable,
        the rest stays in effect and that provision is limited to the minimum necessary; no delay
        in enforcing a right waives it. Neither party is liable for delays caused by events beyond
        its reasonable control, except for payment obligations already due. These Partner Terms
        confer no rights on third parties, except that Stripe's rights under its own agreements are
        unaffected. Headings are for convenience, and "including" means "including without
        limitation".
      </P>
      <P>Questions: legal@myvitae.ai — Vitae AI LLC, operator of myvitae.ai.</P>
    </Section>
  </div>
);

export default PartnerTermsContent;
